// Phase 1C API client — matches actual Phase 1B routes:
//   GET /api/v1/grid/stats
//   GET /api/v1/grid/viewport?x=&y=&w=&h=&cursor=&page_size=
//   GET /api/v1/grid/cell/{x}/{y}
//   GET /api/v1/parcels/{id}
// Read-only. No wallet/SIWE requirement.
//
// Reviewer P0-3: chain validation is centralized here. Every data endpoint
// validates meta.chain_id === expected (Phase 1C: 84532). A mismatch throws
// ChainMismatchError and the caller MUST NOT publish/update viewer state.
//
// Reviewer P0-4: same-origin. This client emits relative URLs `/api/v1/...`.
// No VITE_API_URL routing. Phase 0 auth (src/lib/api.ts) is untouched.
import { ChainMismatchError, validateChainMeta } from "./chain";
import type {
  CellResponse,
  ParcelDetailResponse,
  StatsResponse,
  ViewportResponse,
} from "./types";

// Same-origin only. Do NOT read VITE_API_URL — that was insecure fallback.
export const API_BASE = "";

export class ViewerApiError extends Error {
  code: string;
  status: number;
  aborted: boolean;
  constructor(status: number, code: string, message: string, aborted = false) {
    super(message);
    this.code = code;
    this.status = status;
    this.aborted = aborted;
  }
}

export { ChainMismatchError };

export interface Fetcher {
  (input: string, init?: RequestInit): Promise<Response>;
}

// Injected fetcher for tests; production uses global fetch.
export function makeApi(fetcher: Fetcher = globalThis.fetch.bind(globalThis)) {
  async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const url = `${API_BASE}${path}`;
    let resp: Response;
    try {
      resp = await fetcher(url, { method: "GET", signal });
    } catch (err) {
      if (
        (err instanceof DOMException && err.name === "AbortError") ||
        (err as { name?: string })?.name === "AbortError"
      ) {
        throw new ViewerApiError(0, "ABORTED", "request aborted", true);
      }
      throw new ViewerApiError(0, "NETWORK", (err as Error).message || "network");
    }
    const text = await resp.text();
    let body: unknown = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = text;
    }
    if (!resp.ok) {
      const b = (body as { code?: string; message?: string }) || {};
      throw new ViewerApiError(
        resp.status,
        b.code || `HTTP_${resp.status}`,
        b.message || resp.statusText || "http error",
      );
    }
    return body as T;
  }

  function assertChain<T extends { meta?: { chain_id?: unknown } }>(
    endpoint: string,
    body: T,
  ): T {
    validateChainMeta(body?.meta ?? null, endpoint);
    return body;
  }

  return {
    stats: async (signal?: AbortSignal) => {
      const body = await get<StatsResponse>("/api/v1/grid/stats", signal);
      return assertChain("/grid/stats", body);
    },
    viewport: async (
      params: {
        x: number;
        y: number;
        w: number;
        h: number;
        cursor?: string | null;
        page_size?: number;
      },
      signal?: AbortSignal,
    ) => {
      const q = new URLSearchParams();
      q.set("x", String(params.x));
      q.set("y", String(params.y));
      q.set("w", String(params.w));
      q.set("h", String(params.h));
      q.set("page_size", String(params.page_size ?? 200));
      if (params.cursor) q.set("cursor", params.cursor);
      const body = await get<ViewportResponse>(
        `/api/v1/grid/viewport?${q.toString()}`,
        signal,
      );
      return assertChain("/grid/viewport", body);
    },
    cell: async (x: number, y: number, signal?: AbortSignal) => {
      const body = await get<CellResponse>(`/api/v1/grid/cell/${x}/${y}`, signal);
      return assertChain(`/grid/cell/${x}/${y}`, body);
    },
    parcel: async (id: number, signal?: AbortSignal) => {
      const body = await get<ParcelDetailResponse>(`/api/v1/parcels/${id}`, signal);
      return assertChain(`/parcels/${id}`, body);
    },
  };
}

export type ViewerApi = ReturnType<typeof makeApi>;
